/**
 * The Locker (Milestone 7, designs/Caged Locker, docs/game-design.md §11): try gloves, colours
 * and effects on your fighter, then wear what you own or buy it with coins. Looks only: nothing
 * here changes a fight. The game server checks every purchase.
 */
import { COSMETICS, COSMETIC_KINDS, DEFAULT_LOOK, cosmeticItem, itemLocked, ownsItem, type CosmeticKind, type EffectItem, type Look } from '@caged/shared';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { colorCss, gloveOf, gloveUrl, petOf } from '../../assets/look';
import { assetUrl } from '../../assets/manifest';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { DesktopHeader, TopBar } from '../components/Chrome';
import { Segmented } from '../components/Controls';
import { bodyUrl, FighterArt, fistUrl } from '../components/FighterArt';
import ui from '../components/ui.module.css';
import { useLayoutMode, useManifest, useMyFighter, useMyLook, useScrollTop } from '../hooks';
import s from './LockerScreen.module.css';

const TABS: readonly CosmeticKind[] = ['fist', 'color', 'effect', 'pet'];

/** Whether the player has reached the league's top division (Legend): unlocks Champion. */
function useLegendReached(): boolean {
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  useEffect(() => {
    void load();
  }, [load]);
  if (mine?.status !== 'ok') return false;
  return mine.series.division === 1 || mine.history.some((h) => h.division === 1);
}

/** An item's picture on its card: the glove, the fighter in that colour, the effect's glow, or the pet. */
function Swatch({ kind, id, fighterId }: { kind: CosmeticKind; id: string; fighterId: string }) {
  const manifest = useManifest();
  if (kind === 'fist') {
    const glove = gloveOf(manifest, { ...DEFAULT_LOOK, fist: id });
    return <img className={s.swatchFist} src={glove ? gloveUrl(glove) : fistUrl(manifest, fighterId)} alt="" draggable={false} />;
  }
  if (kind === 'color') {
    const f = manifest?.fighters.find((x) => x.id === fighterId);
    const filter = f ? colorCss({ ...DEFAULT_LOOK, color: id }, f.color) : '';
    return <img className={s.swatchBody} src={bodyUrl(manifest, fighterId, 'idle')} alt="" style={filter ? { filter: `${filter} drop-shadow(0 4px 4px rgba(0,0,0,.5))` } : undefined} draggable={false} />;
  }
  if (kind === 'pet') {
    const pet = petOf(manifest, { ...DEFAULT_LOOK, pet: id });
    return pet ? <img className={s.swatchPet} src={assetUrl(pet.icon)} alt="" draggable={false} /> : <span className={s.swatchNone} />;
  }
  const effect = cosmeticItem('effect', id) as EffectItem | undefined;
  if (!effect || effect.style === 'none') return <span className={s.swatchNone} />;
  return (
    <span className={s.swatchFx} style={{ '--fx': effect.color } as CSSProperties}>
      {(effect.style === 'sparks' || effect.style === 'champion') && (
        <>
          <span className={s.swatchSpark} />
          <span className={s.swatchSpark} />
          <span className={s.swatchSpark} />
        </>
      )}
    </span>
  );
}

export function LockerScreen() {
  const { t } = useTranslation();
  const desktop = useLayoutMode() === 'desktop';
  const progress = useApp((st) => st.progress);
  const fighterId = useMyFighter();
  const playerName = useApp((st) => st.settings.playerName);
  const go = useApp((st) => st.go);
  const buyItem = useApp((st) => st.buyItem);
  const equipItem = useApp((st) => st.equipItem);
  const startQuickFight = useApp((st) => st.startQuickFight);
  const equipped = useMyLook();
  const legend = useLegendReached();
  const [tab, setTab] = useState<CosmeticKind>('fist');
  // What is being tried on: starts as what's worn; picking a card tries it on. When what's worn
  // changes (bought, equipped, or the server's copy arrives), kinds not being tried follow it.
  const [tried, setTried] = useState<Look>(equipped);
  const worn = useRef(equipped);
  useEffect(() => {
    const was = worn.current;
    worn.current = equipped;
    setTried((l) => {
      const next = { ...l };
      for (const k of COSMETIC_KINDS) if (l[k] === was[k]) next[k] = equipped[k];
      return next;
    });
  }, [equipped]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useScrollTop();

  const itemName = (kind: CosmeticKind, id: string) => t(`screens.locker.items.${kind}.${id}`);
  const current = tried[tab];
  const item = cosmeticItem(tab, current) ?? COSMETICS[tab][0]!;
  const owned = ownsItem(progress, tab, current);
  const wearing = equipped[tab] === current;
  const locked = !owned && itemLocked(tab, current, { legend });
  const mode = locked ? 'locked' : wearing ? 'equipped' : owned ? 'equip' : progress.coins >= item.price ? 'buy' : 'poor';

  const act = async () => {
    if (busy || (mode !== 'equip' && mode !== 'buy')) return;
    setBusy(true);
    setFailed(false);
    const ok = mode === 'buy' ? await buyItem(tab, current) : await equipItem(tab, current);
    setBusy(false);
    setFailed(!ok);
  };

  const label =
    mode === 'locked'
      ? t('screens.locker.locked')
      : mode === 'equipped'
        ? t('screens.locker.equipped')
        : mode === 'equip'
          ? t('screens.locker.equip', { item: itemName(tab, current) })
          : mode === 'buy'
            ? t('screens.locker.buy', { item: itemName(tab, current), price: item.price })
            : t('screens.locker.needMore', { coins: item.price - progress.coins });
  const active = mode === 'equip' || mode === 'buy';
  const primary = (
    <div className={s.actions}>
      <button type="button" className={[active ? ui.primary : ui.secondary, s.primary].join(' ')} onClick={() => void act()} disabled={!active || busy} aria-busy={busy}>
        <span className={s.primaryText}>{label}</span>
        <span className={s.primaryIcon} aria-hidden="true">
          {mode === 'equipped' ? '✓' : mode === 'equip' ? '→' : ''}
        </span>
      </button>
      {failed && (
        <p className={s.failed} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
    </div>
  );

  const preview = (
    <div className={s.preview}>
      <div className={s.previewArt}>
        <FighterArt id={fighterId} height={desktop ? 210 : 150} look={tried} alt="" />
      </div>
      <div className={s.name} data-name="">
        {playerName || t('fight.you')}
      </div>
      <div className={s.loadout} aria-label={t('screens.locker.loadoutLabel')}>
        {TABS.map((k) => itemName(k, equipped[k])).join(' · ')}
      </div>
    </div>
  );

  const tabs = (
    <Segmented
      value={tab}
      label={t('screens.locker.title')}
      options={TABS.map((k) => ({ value: k, label: t(`screens.locker.tabs.${k}`) }))}
      onChange={setTab}
      className={s.tabs}
    />
  );

  const grid = (
    <ul className={s.grid}>
      {COSMETICS[tab].map((it) => {
        const own = ownsItem(progress, tab, it.id);
        const on = equipped[tab] === it.id;
        const lockedItem = !own && itemLocked(tab, it.id, { legend });
        let meta: ReactNode;
        if (own) meta = <span className={s.metaOwned}>{t('screens.locker.owned')}</span>;
        else if (lockedItem) meta = <span className={s.metaOwned}>{t('screens.locker.legendOnly')}</span>;
        else
          meta = (
            <span className={s.metaPrice}>
              <span className={s.dot} aria-hidden="true" />
              {it.price}
            </span>
          );
        return (
          <li key={it.id}>
            <button
              type="button"
              className={[s.item, tried[tab] === it.id ? s.itemPicked : '', lockedItem ? s.itemLocked : ''].join(' ')}
              aria-pressed={tried[tab] === it.id}
              onClick={() => {
                setTried((l) => ({ ...l, [tab]: it.id }));
                setFailed(false);
              }}
            >
              {on && <span className={s.on}>{t('screens.locker.on')}</span>}
              <span className={s.swatch}>
                <Swatch kind={tab} id={it.id} fighterId={fighterId} />
              </span>
              <span className={s.itemName}>{itemName(tab, it.id)}</span>
              {meta}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const fight = (
    <button type="button" className={[ui.secondary, s.fight].join(' ')} onClick={() => startQuickFight()}>
      {t('screens.locker.fight')}
      <span className={ui.arrow} aria-hidden="true">
        ▷
      </span>
    </button>
  );

  if (desktop) {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <main className={s.deskMain}>
          <aside className={s.deskAside}>
            <div className={s.deskHero}>
              <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.25),rgba(20,19,18,.9) 75%)" />
              <button type="button" className={s.deskBack} onClick={() => go('profile')}>
                ← {t('screens.nav.profile')}
              </button>
              <span className={[ui.label, s.deskKicker].join(' ')}>{t('screens.locker.title')}</span>
              {preview}
            </div>
            {fight}
          </aside>
          <section className={s.deskContent}>
            <div className={s.deskHead}>
              <div>
                <h1 className={s.title}>{t('screens.locker.title')}</h1>
                <p className={s.subtitle}>{t('screens.locker.subtitle')}</p>
              </div>
              {tabs}
            </div>
            {grid}
            {primary}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <TopBar title={t('screens.locker.title')} onBack={() => go('profile')} />
        {preview}
        <div className={s.stack}>
          {tabs}
          {grid}
          {primary}
          {fight}
        </div>
      </div>
    </div>
  );
}
